import { useTracking } from 'react-tracking';
import Media from 'react-media';

import {
  LoadingSpinner,
  Alert,
  useMount,
  useTheme,
} from '@simplywallst/ui-core';

import { Heading } from '@simplywallst/ui-core/next';

import type { Portfolio } from '@/hooks/portfolio/usePortfolio';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';

import { HoldingsTableLoading } from '../../../../components/HoldingsTable';
import { PortfolioSettings } from '../PortfolioSettings';
import { PortfolioPageHeader } from '../../../../components/PortfolioPageHeader';
import { PortfolioOverviewLoadingState } from '../../../../components/PortfolioOverview';

type Props = {
  portfolio: Portfolio;
};

export const ProcessingState = ({ portfolio }: Props) => {
  const theme = useTheme();
  const { isMobile, isTablet, isDesktop } = useUserAgent();
  const { Track, trackEvent } = useTracking({
    type: 'track',
    modifier: 'importingState',
  });

  useMount(() => {
    trackEvent({ action: 'landed' });
  });

  return (
    <Track>
      <div className="mt-x2 md:my-x2">
        <PortfolioPageHeader portfolio={portfolio}>
          <Media
            defaultMatches={isMobile || isTablet}
            query={{ maxWidth: theme.mdMinRaw }}
          >
            <PortfolioSettings portfolio={portfolio} />
          </Media>
          <div className="grid grid-cols-[max-content_auto] items-center gap-x2">
            <div className="grid grid-cols-[max-content_auto] items-center gap-x1">
              <p className="m-0 text-tiny text-softer">Updating</p>
              <LoadingSpinner light />
            </div>
            <Media
              defaultMatches={isDesktop}
              query={{ minWidth: theme.mdMinRaw }}
            >
              <PortfolioSettings portfolio={portfolio} />
            </Media>
          </div>
        </PortfolioPageHeader>
        <section>
          <div className="m-x2 lg:mx-x0 lg:mb-x3">
            <Alert styleType="loading" data-cy-id="alert-resyncing-message">
              We're analyzing your portfolio data - this should take less than
              30 seconds.
            </Alert>
          </div>
          <PortfolioOverviewLoadingState />
        </section>
        <section>
          <header className="m-x2 my-x1 lg:mx-x0">
            <Heading size="base">Holdings</Heading>
          </header>
          <HoldingsTableLoading />
        </section>
      </div>
    </Track>
  );
};
